import {
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

export type ReelMediaType = "video" | "image"
export interface ReelItem {
  id: string | number
  type: ReelMediaType
  src: string
  duration?: number
  alt?: string
  title?: string
  description?: string
}

interface ReelContextValue {
  data: ReelItem[]
  index: number
  playing: boolean
  muted: boolean
  setIndex: (i: number) => void
  play: () => void
  pause: () => void
  togglePlay: () => void
  toggleMute: () => void
  next: () => void
  prev: () => void
  containerRef: RefObject<HTMLDivElement | null>
  videoRef: RefObject<HTMLVideoElement | null>
  imageRef: RefObject<HTMLImageElement | null>
  progress: number // 0..1
}
const Ctx = createContext<ReelContextValue | null>(null)
export function useReelCtx() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error("Reel components must be used within <Reel>")
  return ctx
}

export function Reel({
  data,
  children,
  autoPlay = true,
  loop = true,
  className,
}: {
  data: ReelItem[]
  children: ReactNode | ((item: ReelItem) => ReactNode)
  autoPlay?: boolean
  loop?: boolean
  className?: string
}) {
  const [index, setIndex] = useState(0)
  const [playing, setPlaying] = useState(autoPlay)
  const [muted, setMuted] = useState(true)
  const [progress, setProgress] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const imageTimerRef = useRef<number | null>(null)
  const videoFallbackTimerRef = useRef<number | null>(null)
  const videoRafRef = useRef<number | null>(null)
  const videoStartAtRef = useRef<number | null>(null)
  const videoBaseProgressRef = useRef<number>(0)
  const lastProgressRef = useRef<number>(0)

  const next = useCallback(() => {
    setIndex((i) => {
      const n = i + 1
      if (n < data.length) return n
      return loop ? 0 : i
    })
  }, [data.length, loop])
  const prev = useCallback(() => {
    setIndex((i) => {
      const p = i - 1
      if (p >= 0) return p
      return loop ? Math.max(0, data.length - 1) : i
    })
  }, [data.length, loop])
  const togglePlay = () => setPlaying((p) => !p)
  const toggleMute = () => setMuted((m) => !m)

  // Handle transitions and progress for current item
  useEffect(() => {
    const current = data[index]
    // Clear timers
    if (imageTimerRef.current) {
      window.clearInterval(imageTimerRef.current)
      imageTimerRef.current = null
    }
    if (videoFallbackTimerRef.current) {
      window.clearInterval(videoFallbackTimerRef.current)
      videoFallbackTimerRef.current = null
    }
    setProgress(0)

    if (!current) return

    if (current.type === "image") {
      const total =
        current.duration && current.duration > 0 ? current.duration : 5
      const startedAt = Date.now()
      if (playing) {
        imageTimerRef.current = window.setInterval(() => {
          const elapsed = (Date.now() - startedAt) / 1000
          const pct = Math.min(1, elapsed / total)
          setProgress(pct)
          if (pct >= 1) {
            window.clearInterval(imageTimerRef.current!)
            imageTimerRef.current = null
            next()
          }
        }, 100)
      }
    } else if (current.type === "video") {
      const v = videoRef.current
      if (v) {
        v.muted = muted
        if (playing) {
          v.play().catch(() => undefined)
        } else {
          v.pause()
        }
      }
    }
  }, [data, index, playing, muted, next])

  // Smooth linear progress for videos via requestAnimationFrame (like images)
  useEffect(() => {
    const current = data[index]
    if (current?.type !== "video") return
    const v = videoRef.current
    if (!v) return

    const dur =
      Number.isFinite(v.duration) && v.duration > 0
        ? v.duration
        : (current.duration ?? 8)

    const startLoop = () => {
      // initialize start timestamp and keep base progress
      videoStartAtRef.current ??= Date.now()
      const tick = () => {
        if (!playing) return
        const elapsed =
          (Date.now() - (videoStartAtRef.current ?? Date.now())) / 1000
        const pct = Math.min(
          1,
          videoBaseProgressRef.current + elapsed / Math.max(0.001, dur),
        )
        if (Math.abs(pct - lastProgressRef.current) > 0.005) {
          lastProgressRef.current = pct
          setProgress(pct)
        }
        if (pct >= 1) {
          // finalize current and advance
          setProgress(1)
          // reset accumulators before moving on
          videoStartAtRef.current = null
          videoBaseProgressRef.current = 0
          lastProgressRef.current = 0
          next()
          return
        }
        videoRafRef.current = window.requestAnimationFrame(tick)
      }
      videoRafRef.current = window.requestAnimationFrame(tick)
    }

    // when playing toggles
    if (playing) {
      videoStartAtRef.current = Date.now()
      startLoop()
      // try to play the actual video element muted for visual sync
      v.muted = muted
      v.play().catch(() => undefined)
    } else {
      // accumulate progress and stop loop
      if (videoStartAtRef.current) {
        const elapsed = (Date.now() - videoStartAtRef.current) / 1000
        videoBaseProgressRef.current = Math.min(
          1,
          videoBaseProgressRef.current + elapsed / Math.max(0.001, dur),
        )
      }
      if (videoRafRef.current) {
        window.cancelAnimationFrame(videoRafRef.current)
        videoRafRef.current = null
      }
      v.pause()
      videoStartAtRef.current = null
    }

    const onEnded = () => {
      setProgress(1)
      next()
    }
    v.addEventListener("ended", onEnded)

    return () => {
      if (videoRafRef.current) {
        window.cancelAnimationFrame(videoRafRef.current)
        videoRafRef.current = null
      }
      v.removeEventListener("ended", onEnded)
      // reset accumulators when index changes away
      videoStartAtRef.current = null
      videoBaseProgressRef.current = 0
    }
  }, [data, index, playing, muted, next])

  const value = useMemo<ReelContextValue>(
    () => ({
      data,
      index,
      playing,
      muted,
      setIndex,
      play: () => setPlaying(true),
      pause: () => setPlaying(false),
      togglePlay,
      toggleMute,
      next,
      prev,
      containerRef,
      videoRef,
      imageRef,
      progress,
    }),
    [data, index, playing, muted, progress, next, prev],
  )

  return (
    <Ctx.Provider value={value}>
      <div
        ref={containerRef}
        className={["relative h-full w-full", className]
          .filter(Boolean)
          .join(" ")}
      >
        {typeof children === "function"
          ? (children as (item: ReelItem) => ReactNode)(data[index])
          : children}
      </div>
    </Ctx.Provider>
  )
}

export function ReelContent({
  children,
}: {
  children: (item: ReelItem) => ReactNode
}) {
  const { data, index } = useReelCtx()
  const current = data[index]
  if (!current) return null
  return <>{children(current)}</>
}

export function ReelItem({ children }: { children: ReactNode }) {
  return <div className="h-full w-full">{children}</div>
}

export function ReelVideo({
  src,
  className,
}: {
  src: string
  className?: string
}) {
  const { videoRef } = useReelCtx()
  return (
    <video
      ref={videoRef}
      className={["h-full w-full object-cover", className]
        .filter(Boolean)
        .join(" ")}
      src={src}
      playsInline
      preload="metadata"
      autoPlay
      muted
      controls={false}
    />
  )
}

export function ReelImage({
  src,
  className,
  alt,
  duration: _duration,
}: {
  src: string
  className?: string
  alt?: string
  duration?: number
}) {
  const { imageRef } = useReelCtx()
  void _duration
  return (
    <img
      ref={imageRef}
      className={["h-full w-full object-cover", className]
        .filter(Boolean)
        .join(" ")}
      src={src}
      alt={alt ?? "image"}
      loading="lazy"
    />
  )
}

export function ReelProgress({ className }: { className?: string }) {
  const { progress } = useReelCtx()
  return (
    <div
      className={[
        "absolute top-0 right-0 left-0 z-20 h-1 bg-black/20",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className="h-full bg-white/80 transition-[width] duration-150 ease-linear"
        style={{ width: `${Math.round(progress * 100)}%` }}
      />
    </div>
  )
}

export function ReelSegmentedProgress({ className }: { className?: string }) {
  const { data, index, progress, containerRef, videoRef, imageRef } =
    useReelCtx()
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    const calc = () => {
      const container = containerRef.current
      if (!container) return
      const cw = container.clientWidth
      const ch = container.clientHeight
      let mw = cw
      let mh = ch

      const v = videoRef.current
      const img = imageRef.current
      if (v && v.videoWidth > 0 && v.videoHeight > 0) {
        mw = v.videoWidth
        mh = v.videoHeight
      } else if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
        mw = img.naturalWidth
        mh = img.naturalHeight
      }
      // escala contain: scale = min(cw/mw, ch/mh)
      const scale = Math.min(cw / (mw || 1), ch / (mh || 1))
      const displayedWidth = Math.round((mw || cw) * scale)
      setWidth(displayedWidth)
    }

    calc()

    const onResize = () => calc()
    window.addEventListener("resize", onResize)

    const v = videoRef.current
    const img = imageRef.current
    const onVideoMeta = () => calc()
    const onImgLoad = () => calc()
    v?.addEventListener("loadedmetadata", onVideoMeta)
    v?.addEventListener("loadeddata", onVideoMeta)
    img?.addEventListener("load", onImgLoad)

    return () => {
      window.removeEventListener("resize", onResize)
      v?.removeEventListener("loadedmetadata", onVideoMeta)
      v?.removeEventListener("loadeddata", onVideoMeta)
      img?.removeEventListener("load", onImgLoad)
    }
  }, [index, data, containerRef, videoRef, imageRef])

  const barStyle = width ? { width } : undefined
  return (
    <div
      className={[
        "pointer-events-none absolute top-0 right-0 left-0 z-20 pt-2",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mx-auto" style={barStyle}>
        <div className="flex gap-1">
          {data.map((_, i) => {
            const filled = i < index ? 1 : i === index ? progress : 0
            return (
              <div
                key={String(i)}
                className="h-1 flex-1 overflow-hidden rounded bg-white/25"
              >
                <div
                  className="h-full rounded bg-white transition-[width] duration-150 ease-linear"
                  style={{ width: `${Math.round(filled * 100)}%` }}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function ReelNavigation({ className }: { className?: string }) {
  const { next, prev } = useReelCtx()
  const onPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    prev()
  }
  const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    next()
  }
  return (
    <div
      className={[
        "absolute inset-0 z-10 flex items-center justify-between px-2",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="h-full w-1/2 cursor-pointer"
        onClick={onPrev}
        aria-label="Anterior"
      />
      <button
        type="button"
        className="h-full w-1/2 cursor-pointer"
        onClick={onNext}
        aria-label="Siguiente"
      />
    </div>
  )
}

export function ReelControls({
  children,
  className,
}: {
  children?: ReactNode
  className?: string
}) {
  return (
    <div
      className={[
        "absolute right-0 bottom-2 left-0 z-20 flex items-center justify-between px-3",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  )
}

export function ReelFooter({
  children,
  className,
}: {
  children?: ReactNode
  className?: string
}) {
  return (
    <div
      className={[
        "absolute right-0 bottom-0 left-0 z-10 bg-gradient-to-t from-black/60 to-transparent p-4",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  )
}

export function ReelPreviousButton({ className }: { className?: string }) {
  const { prev } = useReelCtx()
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    prev()
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full p-2 text-white transition-colors hover:bg-white/20",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Anterior"
    >
      <svg
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>
  )
}

export function ReelNextButton({ className }: { className?: string }) {
  const { next } = useReelCtx()
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    next()
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full p-2 text-white transition-colors hover:bg-white/20",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Siguiente"
    >
      <svg
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>
  )
}

export function ReelPlayButton({ className }: { className?: string }) {
  const { playing, togglePlay } = useReelCtx()
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    togglePlay()
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full p-2 text-white transition-colors hover:bg-white/20",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={playing ? "Pausar" : "Reproducir"}
    >
      {playing ? (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </svg>
      ) : (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  )
}

export function ReelMuteButton({ className }: { className?: string }) {
  const { muted, toggleMute } = useReelCtx()
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    toggleMute()
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full p-2 text-white transition-colors hover:bg-white/20",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={muted ? "Activar sonido" : "Silenciar"}
    >
      {muted ? (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
        </svg>
      ) : (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
      )}
    </button>
  )
}
